import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import { NotInput, NotModal } from '@nebalkon-workspace/ui-kit';

export interface StaffQrPrintCopiesDialogData {
  defaultCopies: number;
}

const MIN_COPIES = 1;
const MAX_COPIES = 99;

@Component({
  selector: 'lib-staff-qr-print-copies-dialog',
  imports: [FormsModule, NotModal, NotInput, CabinetButtonComponent, TranslatePipe],
  template: `
    <lib-not-modal [width]="'min(420px, 96dvw)'">
      <div class="staff-qr-print-copies-dialog">
        <h3 class="staff-qr-print-copies-dialog__title">
          {{ 'admin.users.printClientQrCopiesTitle' | translate }}
        </h3>
        <lib-not-input
          type="number"
          [label]="'admin.users.printClientQrCopiesLabel' | translate"
          [ngModel]="copiesStr()"
          (ngModelChange)="onCopiesChange($event)"
          [hideErrorBlock]="true"
        />
        @if (validationErrorKey()) {
          <p class="staff-qr-print-copies-dialog__error">
            {{ validationErrorKey() | translate }}
          </p>
        }
        <div class="staff-qr-print-copies-dialog__actions">
          <lib-cabinet-button
            type="button"
            variant="secondary"
            secondaryTone="neutral"
            size="md"
            [text]="'CLIENT.CABINET.PROFILE.CANCEL' | translate"
            [action]="{ type: 'action' }"
            (actionEvent)="cancel()"
          />
          <lib-cabinet-button
            type="button"
            variant="primary"
            size="md"
            [text]="'admin.users.printClientQrCopiesConfirm' | translate"
            [action]="{ type: 'action' }"
            (actionEvent)="confirm()"
          />
        </div>
      </div>
    </lib-not-modal>
  `,
  styles: `
    .staff-qr-print-copies-dialog {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 8px 0;
    }

    .staff-qr-print-copies-dialog__title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      text-align: left;
    }

    .staff-qr-print-copies-dialog__error {
      margin: 0;
      color: #b42318;
      font-size: 14px;
      text-align: left;
    }

    .staff-qr-print-copies-dialog__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      justify-content: flex-start;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffQrPrintCopiesDialogComponent {
  readonly data = inject(DIALOG_DATA) as StaffQrPrintCopiesDialogData;
  readonly #dialogRef = inject(DialogRef<number | undefined>);

  protected readonly copiesStr = signal(String(this.data.defaultCopies));
  protected readonly validationErrorKey = signal<string | null>(null);

  protected onCopiesChange(value: string | number | null): void {
    if (value === null || value === undefined) {
      this.copiesStr.set('');
      this.validationErrorKey.set(null);
      return;
    }
    this.copiesStr.set(String(value));
    this.validationErrorKey.set(null);
  }

  protected cancel(): void {
    this.#dialogRef.close(undefined);
  }

  protected confirm(): void {
    const parsed = this.#parseCopies();
    if (parsed === null) {
      this.validationErrorKey.set('admin.users.printClientQrCopiesInvalid');
      return;
    }
    this.#dialogRef.close(parsed);
  }

  #parseCopies(): number | null {
    const raw = this.copiesStr().trim();
    if (raw === '') {
      return null;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < MIN_COPIES || n > MAX_COPIES) {
      return null;
    }
    if (String(n) !== raw.trim()) {
      return null;
    }
    return n;
  }
}
